function normalizeText(value = "") {
  return String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

const ROLE_ALIASES = {
  ganesha_hero_background: ["static_stage_background", "ganesha_hero_background"],
  static_stage_background: ["static_stage_background", "ganesha_hero_background"],
  ganesha_icon_or_silhouette: ["ganesha_center_artwork", "ganesha_icon_or_silhouette"],
  ganesha_center_artwork: ["ganesha_center_artwork", "ganesha_icon_or_silhouette"],
  marigold_frame: ["marigold_top_garland", "marigold_frame", "marigold_side_accents"],
  marigold_top_garland: ["marigold_top_garland", "marigold_frame"],
  closing_background: ["closing_background", "static_stage_background", "ganesha_hero_background"],
  readable_panel: ["readable_panel", "readable_details_card", "venue_details_card"],
  readable_details_card: ["readable_details_card", "readable_panel"],
  venue_details_card: ["venue_details_card", "readable_panel"],
};

function expandRoleAliases(role) {
  const normalizedRole = normalizeText(role);
  return new Set([normalizedRole, ...(ROLE_ALIASES[normalizedRole] || []).map(normalizeText)]);
}

function getElementRole(element = {}) {
  return normalizeText(
    element.role ||
      element.asset_binding?.asset_role ||
      element.asset_binding?.asset_key ||
      element.id ||
      ""
  );
}

function isAssetElement(element = {}) {
  return ["image", "svg", "video"].includes(String(element.type || "").toLowerCase());
}

function isTextElement(element = {}) {
  return String(element.type || "").toLowerCase() === "text";
}

function getSelectedAssetRoles(selectedAssets = []) {
  const selectedRoles = new Set();
  selectedAssets
    .map((asset) => normalizeText(asset.role || asset.asset_role || asset.asset_key))
    .filter(Boolean)
    .forEach((role) => {
      expandRoleAliases(role).forEach((alias) => selectedRoles.add(alias));
    });
  return selectedRoles;
}

function getCompositionAssetRoles(compositionPlan = {}) {
  const roleMap = new Map();
  const scenes = Array.isArray(compositionPlan?.scenes) ? compositionPlan.scenes : [];

  scenes.forEach((scene) => {
    (scene.elements || []).forEach((element) => {
      const binding = element.asset_binding || {};
      const role = normalizeText(binding.asset_role || binding.asset_key || element.role);
      const type = String(element.type || "").toLowerCase();
      const isAssetSlot = ["image", "svg", "video"].includes(type) || Boolean(binding.asset_role || binding.asset_key);
      if (!isAssetSlot || !role || role.startsWith("missing_")) return;

      const placeholderBehavior = String(binding.placeholder_behavior || "").toLowerCase();
      const isOptional =
        placeholderBehavior.includes("hide") ||
        role.includes("watermark") ||
        role.includes("particle") ||
        role.includes("halo") ||
        role.includes("glow");

      if (!roleMap.has(role)) {
        roleMap.set(role, {
          role,
          name: role.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()),
          required: !isOptional,
          source: "composition_slot",
        });
      } else if (!isOptional) {
        roleMap.set(role, { ...roleMap.get(role), required: true });
      }
    });
  });

  return Array.from(roleMap.values());
}

function roleIsSelected(role, selectedRoles) {
  return Array.from(expandRoleAliases(role)).some((alias) => selectedRoles.has(alias));
}

export function evaluateAssetReadiness({ family, selectedAssets = [], compositionPlan = null } = {}) {
  const compositionRoles = getCompositionAssetRoles(compositionPlan);
  const roles = compositionRoles.length
    ? compositionRoles
    : Array.isArray(family?.generatableAssetRoles)
    ? family.generatableAssetRoles
    : [];
  const selectedRoles = getSelectedAssetRoles(selectedAssets);
  const requiredRoles = roles.filter((role) => role.required);
  const recommendedRoles = roles.filter((role) => !role.required);
  const missingRequired = requiredRoles.filter((role) => !roleIsSelected(role.role, selectedRoles));
  const missingRecommended = recommendedRoles.filter((role) => !roleIsSelected(role.role, selectedRoles));
  const requiredCoverage = requiredRoles.length
    ? (requiredRoles.length - missingRequired.length) / requiredRoles.length
    : 1;
  const totalCoverage = roles.length ? (roles.length - missingRequired.length - missingRecommended.length) / roles.length : 1;

  let state = "premium_ready";
  if (missingRequired.length) state = "blocked";
  else if (totalCoverage < 0.75) state = "starter_ready";

  return {
    state,
    score: Math.round((requiredCoverage * 0.75 + totalCoverage * 0.25) * 100),
    requiredCount: requiredRoles.length,
    selectedCount: selectedRoles.size,
    missingRequired,
    missingRecommended,
  };
}

function evaluateScene(scene = {}, index = 0) {
  const elements = Array.isArray(scene.elements) ? scene.elements : [];
  const assetElements = elements.filter(isAssetElement);
  const textElements = elements.filter(isTextElement);
  const animatedElements = elements.filter((element) => Array.isArray(element.animation) && element.animation.length);
  const roles = new Set(elements.map(getElementRole).filter(Boolean));
  const lowerName = normalizeText(`${scene.scene_id || ""}_${scene.name || ""}`);
  const isOpening = index === 0 || lowerName.includes("opening") || lowerName.includes("hero");
  const isClosing = lowerName.includes("closing") || lowerName.includes("blessing");
  const isDetail = !isOpening && !isClosing;
  const issues = [];
  const warnings = [];

  let score = 100;

  if (!elements.length) {
    issues.push("Scene has no elements.");
    score -= 45;
  }

  if (elements.length < 5) {
    warnings.push("Scene has fewer than 5 layers and may feel sparse.");
    score -= 15;
  }

  if (isOpening && assetElements.length < 4) {
    warnings.push("Opening scene needs more visual layers.");
    score -= 15;
  }

  if (isOpening && animatedElements.length < 3) {
    warnings.push("Opening scene has limited motion.");
    score -= 12;
  }

  if (isDetail && assetElements.length < 2) {
    warnings.push("Detail scene needs panel/decorative support, not only text.");
    score -= 14;
  }

  if (isDetail && textElements.length < 2) {
    warnings.push("Detail scene should split copy into editable hierarchy.");
    score -= 10;
  }

  if (isDetail && !Array.from(roles).some((role) => role.includes("panel") || role.includes("card"))) {
    warnings.push("Detail scene is missing a readable card or panel.");
    score -= 14;
  }

  const missingAssetNodes = assetElements.filter(
    (element) =>
      element.asset_binding?.source_strategy === "placeholder_until_available" ||
      element.asset_binding?.placeholder_behavior ||
      !element.asset_binding?.media_url
  );
  if (missingAssetNodes.length) {
    warnings.push(`${missingAssetNodes.length} asset layer(s) may be placeholders or unresolved.`);
    score -= Math.min(20, missingAssetNodes.length * 5);
  }

  return {
    sceneId: scene.scene_id || scene.page_id || `scene-${index + 1}`,
    name: scene.name || `Scene ${index + 1}`,
    score: Math.max(0, Math.min(100, Math.round(score))),
    elementCount: elements.length,
    assetCount: assetElements.length,
    textCount: textElements.length,
    animatedCount: animatedElements.length,
    issues,
    warnings,
  };
}

export function evaluateCompositionQuality({ compositionPlan, selectedAssets = [], family } = {}) {
  const scenes = Array.isArray(compositionPlan?.scenes) ? compositionPlan.scenes : [];
  const assetReadiness = evaluateAssetReadiness({ family, selectedAssets, compositionPlan });
  const sceneScores = scenes.map(evaluateScene);
  const blockingIssues = [];
  const warnings = [];

  if (!scenes.length) {
    blockingIssues.push("No scenes were returned in the composition plan.");
  }

  if (assetReadiness.state === "blocked") {
    blockingIssues.push(
      `${assetReadiness.missingRequired.length} required asset role(s) are missing.`
    );
  }

  sceneScores.forEach((scene) => {
    scene.issues.forEach((issue) => blockingIssues.push(`${scene.name}: ${issue}`));
    scene.warnings.forEach((warning) => warnings.push(`${scene.name}: ${warning}`));
  });

  const averageSceneScore = sceneScores.length
    ? sceneScores.reduce((total, scene) => total + scene.score, 0) / sceneScores.length
    : 0;
  const score = Math.round(averageSceneScore * 0.7 + assetReadiness.score * 0.3);

  return {
    score: Math.max(0, Math.min(100, score)),
    status:
      blockingIssues.length || score < 55
        ? "needs_assets_or_repair"
        : score < 75
        ? "starter"
        : "strong",
    assetReadiness,
    sceneScores,
    blockingIssues,
    warnings,
  };
}
